import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { TEN_MINUTES } from '@/constants/time';
import { useCognitoGroups } from '@/hooks/useCognitoGroups';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import type { InboxPage } from '@/services/conversations/inbox';
import { splitServerQuestionGroups } from '@/services/conversations/inboxQuestionGroups';
import type { InquiryV2 } from '@/services/getInquiriesV2';
import { getInquiryV2 } from '@/services/getInquiryV2';
import { useInboxQuestionGroupCardsOptIn } from '@/services/stores/inboxQuestionGroupCardsOptInStore';
import {
  buildQuestionGroups,
  findMissingOriginalIds,
  selectOriginalIdsToFetch,
  type QuestionGroupSplit,
} from '@/util/questionGroups';

/**
 * Whether the inbox nests relay threads under their original. The Primum group is required in
 * every inquiry group, so the feature stays off every expert and submitter surface. Past that gate it is on
 * when the flag is on for everyone or the staff member turned it on for this device. Group
 * membership reports false while unresolved, so the list starts flat.
 */
export function shouldShowInboxQuestionGroupCards(
  isFlagEnabled: boolean,
  isPrimumUser: boolean,
  hasOptedIn = false
) {
  return isPrimumUser && (isFlagEnabled || hasOptedIn);
}

export function useInboxQuestionGroupCardsEnabled() {
  const isFlagEnabled = useFeatureFlag(FEATURE_FLAGS.INBOX_INQUIRY_GROUP_CARDS);

  const hasOptedIn = useInboxQuestionGroupCardsOptIn();

  const { isPrimumUser } = useCognitoGroups();

  return shouldShowInboxQuestionGroupCards(isFlagEnabled, isPrimumUser, hasOptedIn);
}

/** How many originals may be requested at once. */
export const MAX_ORIGIN_REQUESTS_IN_FLIGHT = 8;

const ORIGINAL_STALE_TIME = TEN_MINUTES;

/**
 * Its own key, not the single-inquiry key. The inbox's cache patches write to every query under the
 * inquiry list prefix and expect pages, so an original stored there would be patched as a list.
 */
const originalQueryKey = (conversationId: string) => ['inboxQuestionGroupOriginal', conversationId];

const fetchOriginal = async (conversationId: string) => {
  const original = await getInquiryV2({ conversationId });

  if (!original) {
    throw new Error('Original inquiry not found');
  }

  return original;
};

/**
 * Splits a tab's inquiries into staff inquiry groups and the rest. With cards off the rest is the input
 * itself, so the flat list is untouched and nothing is fetched.
 *
 * When the tab's pages came back grouped by the server, `pages` carries the inquiry groups, and they are
 * used as they are: the server already put closed relays inside an open inquiry group and read the
 * original, so nothing is grouped or fetched here. Pages with no inquiry groups, from a viewer who did not
 * ask, an old server or a refused request, take the client path below.
 *
 * A relay that rose to the top while its original sits on a page not yet loaded would never form a
 * inquiry group, so the original is fetched by its conversation id, cached by that id so every tab and
 * render shares one request, and nested with the relays that are loaded. A fetch that fails or is
 * refused leaves the relay a flat row. A fetched original appears only inside its inquiry group.
 */
export function useInboxQuestionGroups(
  inquiries: InquiryV2[],
  pages?: readonly (InboxPage | undefined)[]
): QuestionGroupSplit {
  const enabled = useInboxQuestionGroupCardsEnabled();

  const queryClient = useQueryClient();

  const serverSplit = useMemo(
    () => (enabled ? splitServerQuestionGroups(pages, inquiries) : null),
    [enabled, pages, inquiries]
  );

  const groupsOnClient = enabled && !serverSplit;

  const missingIds = useMemo(
    () => (groupsOnClient ? findMissingOriginalIds(inquiries) : []),
    [groupsOnClient, inquiries]
  );

  const allowed = selectOriginalIdsToFetch(
    missingIds,
    (conversationId) => {
      const state = queryClient.getQueryState(originalQueryKey(conversationId));

      return state?.status === 'success' || state?.status === 'error';
    },
    MAX_ORIGIN_REQUESTS_IN_FLIGHT
  );

  const results = useQueries({
    queries: missingIds.map((conversationId) => ({
      queryKey: originalQueryKey(conversationId),
      queryFn: () => fetchOriginal(conversationId),
      gcTime: ORIGINAL_STALE_TIME,
      enabled: allowed.has(conversationId),
      retry: false,
      staleTime: ORIGINAL_STALE_TIME,
      refetchOnWindowFocus: false,
    })),
  });

  const fetched = results.flatMap((result) => (result.data ? [result.data as InquiryV2] : []));

  const fetchedKey = fetched.map((original) => original.conversationId).join(',');

  return useMemo(() => {
    if (serverSplit) {
      return serverSplit;
    }

    return enabled
      ? buildQuestionGroups(inquiries, fetched)
      : { questionGroups: [], remaining: inquiries };
    // `fetched` is a fresh array each render, so it is keyed by the ids it holds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverSplit, enabled, inquiries, fetchedKey]);
}
